// Translations for content creation modals across 13 languages
// Namespaces: gim (Gim Toli creation), pod (Podcast creation),
// story (Story creation), spo (Sponsor picker), mon (Monetization bubble)

import { useLanguage } from '../context/LanguageContext';

export const creationTranslations = {
  en: {
    gim: {
      modalTitle: 'Gim Toli / Vocals',
      replyTo: 'Reply to {name}',
      replyToDefault: 'a Gim Toli',
      audio: 'Audio',
      tabRecord: 'Record',
      tabUpload: 'Upload',
      pickAudioFile: 'Choose an audio file',
      audioLimits: 'Max 60 seconds • Supported: MP3, WAV, M4A, etc.',
      recording: 'Recording…',
      titlePlaceholder: 'e.g. My take on this movie…',
      background: 'Background image (optional)',
      backgroundPick: 'Choose an image',
      effectsLabel: 'Audio effects (optional)',
      effectsApplying: 'Applying…',
      proVoices: 'Pro voices',
      rerecord: 'Re-record',
      publish: 'Publish',
      tapToRecord: 'Tap the button to start recording',
      micHint: '💡 If the microphone does not work, allow mic access in your browser settings',
      errNoAudio: 'No audio recorded',
      errTooLong: "Audio must be shorter than {n} seconds",
      errNotAudio: 'Please pick an audio file',
      errTooBig: 'File too large (max 10MB)',
      okLoaded: 'Audio loaded successfully!',
      errUpload: 'Upload error',
      errEffect: "Could not apply the effect",
      okEffect: '{emoji} {label} applied',
      errMicDenied: 'Microphone permission denied. Click the 🔒 icon in the address bar to allow the microphone.',
      errMicNotFound: 'No microphone detected on this device.',
      errMicBusy: 'The microphone is used by another app.',
      errMicGeneric: 'Cannot access the microphone. Ensure HTTPS and permissions are allowed.'
    },
    pod: {
      modalTitle: 'Create a Podcast',
      cover: 'Cover image',
      add: 'Add',
      title: 'Title *',
      titlePlaceholder: 'The title of your podcast',
      category: 'Category',
      selectCategory: 'Select a category',
      guests: 'Guests (comma separated)',
      guestsPlaceholder: 'John Smith, Jane Doe',
      audioFile: 'Audio file *',
      pickFile: 'Click to pick a file',
      audioFormats: 'MP3, WAV, AAC (max 500 MB)',
      publish: 'Publish',
      errNoAudio: 'Please pick an audio file',
      errNoTitle: 'Title is required',
      errUpload: 'Upload error',
      catCinema: 'Cinema',
      catSeries: 'Series',
      catMaking: 'Making-of',
      catTech: 'Tech',
      catMusic: 'Film music',
      catInterviews: 'Interviews',
      catReviews: 'Reviews'
    },
    story: {
      modalTitle: 'New Story',
      subtitle: 'Share an image or a video under 60 seconds',
      openCamera: 'Take with camera',
      tapToPick: 'Click to select',
      hint: 'Image or video (<60s)',
      images: 'Images',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Videos',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Change',
      publishing: 'Publishing…',
      publishRaw: 'Publish as-is',
      addText: 'Add text',
      published: 'Story published!',
      errTooBig: 'File too large (max 50MB)',
      errFormat: 'Unsupported format',
      errTooLong: 'Video must be under 60 seconds',
      errPick: 'Please pick a file',
      errPublish: 'Publication error'
    },
    spo: {
      title: 'Choose a sponsor',
      close: 'Close',
      lockedBanner: 'Monetization unlocks at 5,000 followers. You are at {n}. You can still share without a sponsor.',
      shareWithout: 'Share without sponsor',
      searchPlaceholder: 'Search a sponsor (verified PRO account)…',
      onlyProNote: 'Only verified PRO accounts ✓ are eligible.',
      searching: 'Searching…',
      noResults: 'No sponsor found.',
      activeOffers: 'Active sponsorship offers',
      offersHint: 'Apply to a public offer directly. The sponsor will review your application before publication.',
      proposal: 'Proposal',
      amount: 'Amount',
      minDur: 'Minimum duration: {n}s. You must talk about the sponsor for at least 10s. The sponsor will review your stats before validation.',
      willReview: 'The sponsor will review your stats before validation.',
      send: 'Send request',
      sending: 'Sending…',
      applyOffer: 'Apply to the offer',
      applyTitle: 'Offer application',
      talkAtLeast: 'You must talk about the sponsor for at least {n}s.',
      byShort: 'By',
      pickFirst: 'Choose a sponsor or an offer',
      invalidAmount: 'Invalid amount',
      okSubmitted: 'Request sent — waiting for validation',
      errRequest: 'Error while submitting'
    },
    mon: {
      title: 'Monetization',
      body: 'From 5,000 followers, you can attach a sponsor to your content and receive direct payments in 15+ currencies.',
      cta: 'Learn more',
      dismiss: 'dismiss'
    }
  },
  fr: {
    gim: {
      modalTitle: 'Gim Toli / Vocaux',
      replyTo: 'En réponse à {name}',
      replyToDefault: 'un Gim Toli',
      audio: 'Audio',
      tabRecord: 'Enregistrer',
      tabUpload: 'Télécharger',
      pickAudioFile: 'Choisir un fichier audio',
      audioLimits: 'Max 60 secondes • Formats : MP3, WAV, M4A, etc.',
      recording: 'Enregistrement…',
      titlePlaceholder: 'Ex : Mon avis sur ce film…',
      background: 'Image de fond (optionnel)',
      backgroundPick: 'Choisir une image',
      effectsLabel: 'Effets audio (facultatif)',
      effectsApplying: 'Application…',
      proVoices: 'Voix pro',
      rerecord: 'Réenregistrer',
      publish: 'Publier',
      tapToRecord: "Appuie sur le bouton pour commencer l'enregistrement",
      micHint: "💡 Si le micro ne fonctionne pas, autorise l'accès dans les paramètres du navigateur",
      errNoAudio: 'Aucun audio enregistré',
      errTooLong: "L'audio doit durer moins de {n} secondes",
      errNotAudio: 'Veuillez sélectionner un fichier audio',
      errTooBig: 'Fichier trop volumineux (max 10 Mo)',
      okLoaded: 'Audio chargé avec succès !',
      errUpload: "Erreur lors de l'envoi",
      errEffect: "Impossible d'appliquer l'effet",
      okEffect: '{emoji} {label} appliqué',
      errMicDenied: "Permission du microphone refusée. Cliquez sur l'icône 🔒 dans la barre d'adresse pour autoriser le microphone.",
      errMicNotFound: 'Aucun microphone détecté sur cet appareil.',
      errMicBusy: 'Le microphone est déjà utilisé par une autre application.',
      errMicGeneric: "Impossible d'accéder au microphone. Assurez-vous d'utiliser HTTPS et d'autoriser les permissions."
    },
    pod: {
      modalTitle: 'Créer un Podcast',
      cover: 'Image de couverture',
      add: 'Ajouter',
      title: 'Titre *',
      titlePlaceholder: 'Le titre de votre podcast',
      category: 'Catégorie',
      selectCategory: 'Sélectionner une catégorie',
      guests: 'Invités (séparés par des virgules)',
      guestsPlaceholder: 'Jean Dupont, Marie Martin',
      audioFile: 'Fichier audio *',
      pickFile: 'Cliquez pour choisir un fichier',
      audioFormats: 'MP3, WAV, AAC (max 500 Mo)',
      publish: 'Publier',
      errNoAudio: 'Veuillez sélectionner un fichier audio',
      errNoTitle: 'Le titre est requis',
      errUpload: "Erreur lors de l'envoi",
      catCinema: 'Cinéma',
      catSeries: 'Séries',
      catMaking: 'Making-of',
      catTech: 'Tech',
      catMusic: 'Musiques de films',
      catInterviews: 'Interviews',
      catReviews: 'Critiques'
    },
    story: {
      modalTitle: 'Nouvelle Story',
      subtitle: 'Partagez une image ou une vidéo de moins de 60 secondes',
      openCamera: 'Prendre avec la caméra',
      tapToPick: 'Cliquez pour sélectionner',
      hint: 'Image ou vidéo (<60s)',
      images: 'Images',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Vidéos',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Changer',
      publishing: 'Publication…',
      publishRaw: 'Publier tel quel',
      addText: 'Ajouter du texte',
      published: 'Story publiée !',
      errTooBig: 'Fichier trop volumineux (max 50 Mo)',
      errFormat: 'Format non supporté',
      errTooLong: 'La vidéo doit durer moins de 60 secondes',
      errPick: 'Veuillez sélectionner un fichier',
      errPublish: 'Erreur lors de la publication'
    },
    spo: {
      title: 'Choisir un sponsor',
      close: 'Fermer',
      lockedBanner: "La monétisation se débloque à 5 000 abonnés. Tu en es à {n}. Tu peux toujours partager ton contenu sans sponsor.",
      shareWithout: 'Partager sans sponsor',
      searchPlaceholder: 'Rechercher un sponsor (compte PRO vérifié)…',
      onlyProNote: 'Seuls les comptes PRO vérifiés ✓ sont éligibles.',
      searching: 'Recherche…',
      noResults: 'Aucun sponsor trouvé.',
      activeOffers: 'Offres de sponsoring actives',
      offersHint: "Postule directement à une offre publique. Le sponsor reverra ta candidature avant publication.",
      proposal: 'Proposition',
      amount: 'Montant',
      minDur: 'Durée minimum : {n}s. Tu dois parler du sponsor au moins 10s. Le sponsor reverra tes statistiques avant validation.',
      willReview: 'Le sponsor reverra tes statistiques avant validation.',
      send: 'Envoyer la demande',
      sending: 'Envoi…',
      applyOffer: "Postuler à l'offre",
      applyTitle: "Postulation à l'offre",
      talkAtLeast: 'Tu dois parler du sponsor au moins {n}s.',
      byShort: 'Par',
      pickFirst: 'Choisis un sponsor ou une offre',
      invalidAmount: 'Montant invalide',
      okSubmitted: 'Demande envoyée — en attente de validation',
      errRequest: 'Erreur lors de la demande'
    },
    mon: {
      title: 'Monétisation',
      body: "À partir de 5 000 abonnés, vous pouvez attacher un sponsor à vos contenus et recevoir des paiements directs en plus de 15 devises.",
      cta: 'En savoir plus',
      dismiss: 'fermer'
    }
  },
  es: {
    gim: {
      modalTitle: 'Gim Toli / Vocales',
      replyTo: 'En respuesta a {name}',
      replyToDefault: 'un Gim Toli',
      audio: 'Audio',
      tabRecord: 'Grabar',
      tabUpload: 'Subir',
      pickAudioFile: 'Elegir un archivo de audio',
      audioLimits: 'Máx 60 segundos • MP3, WAV, M4A, etc.',
      recording: 'Grabando…',
      titlePlaceholder: 'Ej: Mi opinión sobre esta película…',
      background: 'Imagen de fondo (opcional)',
      backgroundPick: 'Elegir una imagen',
      effectsLabel: 'Efectos de audio (opcional)',
      effectsApplying: 'Aplicando…',
      proVoices: 'Voces pro',
      rerecord: 'Volver a grabar',
      publish: 'Publicar',
      tapToRecord: 'Pulsa el botón para empezar a grabar',
      micHint: '💡 Si el micrófono no funciona, permite el acceso en la configuración del navegador',
      errNoAudio: 'Ningún audio grabado',
      errTooLong: 'El audio debe durar menos de {n} segundos',
      errNotAudio: 'Selecciona un archivo de audio',
      errTooBig: 'Archivo demasiado grande (máx 10MB)',
      okLoaded: '¡Audio cargado con éxito!',
      errUpload: 'Error al enviar',
      errEffect: 'No se pudo aplicar el efecto',
      okEffect: '{emoji} {label} aplicado',
      errMicDenied: 'Permiso de micrófono denegado. Haz clic en el icono 🔒 de la barra de direcciones para permitirlo.',
      errMicNotFound: 'No se detectó ningún micrófono en este dispositivo.',
      errMicBusy: 'El micrófono ya está siendo usado por otra aplicación.',
      errMicGeneric: 'No se puede acceder al micrófono. Asegúrate de usar HTTPS y otorgar permisos.'
    },
    pod: {
      modalTitle: 'Crear un Podcast',
      cover: 'Imagen de portada',
      add: 'Añadir',
      title: 'Título *',
      titlePlaceholder: 'El título de tu podcast',
      category: 'Categoría',
      selectCategory: 'Selecciona una categoría',
      guests: 'Invitados (separados por comas)',
      guestsPlaceholder: 'Juan Pérez, María López',
      audioFile: 'Archivo de audio *',
      pickFile: 'Haz clic para elegir un archivo',
      audioFormats: 'MP3, WAV, AAC (máx 500 MB)',
      publish: 'Publicar',
      errNoAudio: 'Selecciona un archivo de audio',
      errNoTitle: 'El título es obligatorio',
      errUpload: 'Error al enviar',
      catCinema: 'Cine',
      catSeries: 'Series',
      catMaking: 'Cómo se hizo',
      catTech: 'Tecnología',
      catMusic: 'Música de películas',
      catInterviews: 'Entrevistas',
      catReviews: 'Críticas'
    },
    story: {
      modalTitle: 'Nueva Story',
      subtitle: 'Comparte una imagen o un vídeo de menos de 60 segundos',
      openCamera: 'Tomar con la cámara',
      tapToPick: 'Haz clic para seleccionar',
      hint: 'Imagen o vídeo (<60s)',
      images: 'Imágenes',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Vídeos',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Cambiar',
      publishing: 'Publicando…',
      publishRaw: 'Publicar tal cual',
      addText: 'Añadir texto',
      published: '¡Story publicada!',
      errTooBig: 'Archivo demasiado grande (máx 50MB)',
      errFormat: 'Formato no admitido',
      errTooLong: 'El vídeo debe durar menos de 60 segundos',
      errPick: 'Selecciona un archivo',
      errPublish: 'Error al publicar'
    },
    spo: {
      title: 'Elegir un patrocinador',
      close: 'Cerrar',
      lockedBanner: 'La monetización se desbloquea con 5 000 seguidores. Tienes {n}. Aún puedes compartir sin patrocinador.',
      shareWithout: 'Compartir sin patrocinador',
      searchPlaceholder: 'Buscar un patrocinador (cuenta PRO verificada)…',
      onlyProNote: 'Solo las cuentas PRO verificadas ✓ son elegibles.',
      searching: 'Buscando…',
      noResults: 'No se encontró ningún patrocinador.',
      activeOffers: 'Ofertas de patrocinio activas',
      offersHint: 'Postula directamente a una oferta pública. El patrocinador revisará tu candidatura antes de la publicación.',
      proposal: 'Propuesta',
      amount: 'Importe',
      minDur: 'Duración mínima: {n}s. Debes hablar del patrocinador al menos 10s. Revisará tus estadísticas antes de validar.',
      willReview: 'El patrocinador revisará tus estadísticas antes de validar.',
      send: 'Enviar solicitud',
      sending: 'Enviando…',
      applyOffer: 'Postular a la oferta',
      applyTitle: 'Postulación a la oferta',
      talkAtLeast: 'Debes hablar del patrocinador al menos {n}s.',
      byShort: 'Por',
      pickFirst: 'Elige un patrocinador o una oferta',
      invalidAmount: 'Importe inválido',
      okSubmitted: 'Solicitud enviada — en espera de validación',
      errRequest: 'Error al enviar la solicitud'
    },
    mon: {
      title: 'Monetización',
      body: 'A partir de 5 000 seguidores puedes vincular un patrocinador a tu contenido y recibir pagos directos en más de 15 divisas.',
      cta: 'Saber más',
      dismiss: 'cerrar'
    }
  },
  ar: {
    gim: {
      modalTitle: 'جيم تولي / صوتيات',
      replyTo: 'رد على {name}',
      replyToDefault: 'جيم تولي',
      audio: 'صوت',
      tabRecord: 'تسجيل',
      tabUpload: 'رفع',
      pickAudioFile: 'اختر ملفًا صوتيًا',
      audioLimits: 'حد أقصى 60 ثانية • MP3، WAV، M4A، إلخ.',
      recording: 'جارٍ التسجيل…',
      titlePlaceholder: 'مثال: رأيي في هذا الفيلم…',
      background: 'صورة الخلفية (اختياري)',
      backgroundPick: 'اختر صورة',
      effectsLabel: 'مؤثرات صوتية (اختياري)',
      effectsApplying: 'جارٍ التطبيق…',
      proVoices: 'أصوات احترافية',
      rerecord: 'إعادة التسجيل',
      publish: 'نشر',
      tapToRecord: 'اضغط الزر لبدء التسجيل',
      micHint: '💡 إذا لم يعمل الميكروفون، اسمح بالوصول من إعدادات المتصفح',
      errNoAudio: 'لا يوجد صوت مسجل',
      errTooLong: 'يجب أن يكون الصوت أقل من {n} ثانية',
      errNotAudio: 'يرجى اختيار ملف صوتي',
      errTooBig: 'الملف كبير جدًا (الحد 10 ميغابايت)',
      okLoaded: 'تم تحميل الصوت بنجاح!',
      errUpload: 'خطأ أثناء الإرسال',
      errEffect: 'تعذر تطبيق المؤثر',
      okEffect: '{emoji} تم تطبيق {label}',
      errMicDenied: 'تم رفض إذن الميكروفون. اضغط على أيقونة 🔒 في شريط العنوان للسماح.',
      errMicNotFound: 'لم يتم اكتشاف أي ميكروفون على هذا الجهاز.',
      errMicBusy: 'الميكروفون مستخدم من تطبيق آخر.',
      errMicGeneric: 'لا يمكن الوصول إلى الميكروفون. تأكد من HTTPS ومنح الأذونات.'
    },
    pod: {
      modalTitle: 'إنشاء بودكاست',
      cover: 'صورة الغلاف',
      add: 'إضافة',
      title: 'العنوان *',
      titlePlaceholder: 'عنوان البودكاست',
      category: 'الفئة',
      selectCategory: 'اختر فئة',
      guests: 'الضيوف (مفصولين بفواصل)',
      guestsPlaceholder: 'محمد أحمد، ليلى علي',
      audioFile: 'ملف صوتي *',
      pickFile: 'انقر لاختيار ملف',
      audioFormats: 'MP3، WAV، AAC (حد 500 ميغابايت)',
      publish: 'نشر',
      errNoAudio: 'يرجى اختيار ملف صوتي',
      errNoTitle: 'العنوان مطلوب',
      errUpload: 'خطأ أثناء الإرسال',
      catCinema: 'سينما',
      catSeries: 'مسلسلات',
      catMaking: 'كواليس',
      catTech: 'تقنية',
      catMusic: 'موسيقى الأفلام',
      catInterviews: 'مقابلات',
      catReviews: 'مراجعات'
    },
    story: {
      modalTitle: 'قصة جديدة',
      subtitle: 'شارك صورة أو فيديو أقل من 60 ثانية',
      openCamera: 'التقاط بالكاميرا',
      tapToPick: 'انقر للاختيار',
      hint: 'صورة أو فيديو (أقل من 60 ثانية)',
      images: 'صور',
      imgFormats: 'JPG، PNG، WebP',
      videos: 'فيديوهات',
      vidFormats: 'MP4، WebM (<60 ثانية)',
      change: 'تغيير',
      publishing: 'جارٍ النشر…',
      publishRaw: 'نشر كما هو',
      addText: 'إضافة نص',
      published: 'تم نشر القصة!',
      errTooBig: 'الملف كبير جدًا (حد 50 ميغابايت)',
      errFormat: 'صيغة غير مدعومة',
      errTooLong: 'يجب أن يكون الفيديو أقل من 60 ثانية',
      errPick: 'يرجى اختيار ملف',
      errPublish: 'خطأ أثناء النشر'
    },
    spo: {
      title: 'اختر راعيًا',
      close: 'إغلاق',
      lockedBanner: 'يتم فتح تحقيق الدخل عند 5 000 متابع. لديك {n}. يمكنك المشاركة دون راعٍ.',
      shareWithout: 'مشاركة بدون راعٍ',
      searchPlaceholder: 'ابحث عن راعٍ (حساب PRO موثّق)…',
      onlyProNote: 'فقط حسابات PRO الموثّقة ✓ مؤهلة.',
      searching: 'جارٍ البحث…',
      noResults: 'لم يتم العثور على أي راعٍ.',
      activeOffers: 'عروض الرعاية النشطة',
      offersHint: 'قدّم مباشرة على عرض عام. سيراجع الراعي طلبك قبل النشر.',
      proposal: 'عرض',
      amount: 'المبلغ',
      minDur: 'المدة الدنيا: {n} ثانية. يجب التحدث عن الراعي 10 ثوانٍ على الأقل. سيراجع إحصائياتك قبل التحقق.',
      willReview: 'سيراجع الراعي إحصائياتك قبل التحقق.',
      send: 'إرسال الطلب',
      sending: 'جارٍ الإرسال…',
      applyOffer: 'التقديم على العرض',
      applyTitle: 'طلب على العرض',
      talkAtLeast: 'يجب التحدث عن الراعي {n} ثانية على الأقل.',
      byShort: 'بواسطة',
      pickFirst: 'اختر راعيًا أو عرضًا',
      invalidAmount: 'مبلغ غير صالح',
      okSubmitted: 'تم إرسال الطلب — بانتظار التحقق',
      errRequest: 'خطأ أثناء الإرسال'
    },
    mon: {
      title: 'تحقيق الدخل',
      body: 'ابتداءً من 5 000 متابع، يمكنك إرفاق راعٍ بمحتواك وتلقي مدفوعات مباشرة بأكثر من 15 عملة.',
      cta: 'اعرف المزيد',
      dismiss: 'إغلاق'
    }
  },
  pt: {
    gim: {
      modalTitle: 'Gim Toli / Vocais',
      replyTo: 'Em resposta a {name}',
      replyToDefault: 'um Gim Toli',
      audio: 'Áudio',
      tabRecord: 'Gravar',
      tabUpload: 'Enviar',
      pickAudioFile: 'Escolher um arquivo de áudio',
      audioLimits: 'Máx 60 segundos • MP3, WAV, M4A, etc.',
      recording: 'Gravando…',
      titlePlaceholder: 'Ex: Minha opinião sobre este filme…',
      background: 'Imagem de fundo (opcional)',
      backgroundPick: 'Escolher uma imagem',
      effectsLabel: 'Efeitos de áudio (opcional)',
      effectsApplying: 'Aplicando…',
      proVoices: 'Vozes pro',
      rerecord: 'Regravar',
      publish: 'Publicar',
      tapToRecord: 'Toque no botão para começar a gravar',
      micHint: '💡 Se o microfone não funcionar, permita o acesso nas configurações do navegador',
      errNoAudio: 'Nenhum áudio gravado',
      errTooLong: 'O áudio deve durar menos que {n} segundos',
      errNotAudio: 'Selecione um arquivo de áudio',
      errTooBig: 'Arquivo muito grande (máx 10MB)',
      okLoaded: 'Áudio carregado com sucesso!',
      errUpload: 'Erro no envio',
      errEffect: 'Não foi possível aplicar o efeito',
      okEffect: '{emoji} {label} aplicado',
      errMicDenied: 'Permissão de microfone negada. Clique no ícone 🔒 na barra de endereço.',
      errMicNotFound: 'Nenhum microfone detectado.',
      errMicBusy: 'O microfone já está em uso por outro app.',
      errMicGeneric: 'Não foi possível acessar o microfone. Use HTTPS e conceda permissões.'
    },
    pod: {
      modalTitle: 'Criar um Podcast',
      cover: 'Imagem de capa',
      add: 'Adicionar',
      title: 'Título *',
      titlePlaceholder: 'O título do seu podcast',
      category: 'Categoria',
      selectCategory: 'Selecione uma categoria',
      guests: 'Convidados (separados por vírgula)',
      guestsPlaceholder: 'João Silva, Maria Souza',
      audioFile: 'Arquivo de áudio *',
      pickFile: 'Clique para escolher um arquivo',
      audioFormats: 'MP3, WAV, AAC (máx 500 MB)',
      publish: 'Publicar',
      errNoAudio: 'Selecione um arquivo de áudio',
      errNoTitle: 'O título é obrigatório',
      errUpload: 'Erro no envio',
      catCinema: 'Cinema',
      catSeries: 'Séries',
      catMaking: 'Making-of',
      catTech: 'Tecnologia',
      catMusic: 'Músicas de filmes',
      catInterviews: 'Entrevistas',
      catReviews: 'Críticas'
    },
    story: {
      modalTitle: 'Nova Story',
      subtitle: 'Compartilhe uma imagem ou vídeo com menos de 60 segundos',
      openCamera: 'Tirar com a câmera',
      tapToPick: 'Clique para selecionar',
      hint: 'Imagem ou vídeo (<60s)',
      images: 'Imagens',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Vídeos',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Trocar',
      publishing: 'Publicando…',
      publishRaw: 'Publicar como está',
      addText: 'Adicionar texto',
      published: 'Story publicada!',
      errTooBig: 'Arquivo muito grande (máx 50MB)',
      errFormat: 'Formato não suportado',
      errTooLong: 'O vídeo deve durar menos de 60 segundos',
      errPick: 'Selecione um arquivo',
      errPublish: 'Erro na publicação'
    },
    spo: {
      title: 'Escolher um patrocinador',
      close: 'Fechar',
      lockedBanner: 'A monetização é desbloqueada aos 5 000 seguidores. Você tem {n}. Ainda pode compartilhar sem patrocinador.',
      shareWithout: 'Compartilhar sem patrocinador',
      searchPlaceholder: 'Buscar um patrocinador (conta PRO verificada)…',
      onlyProNote: 'Somente contas PRO verificadas ✓ são elegíveis.',
      searching: 'Buscando…',
      noResults: 'Nenhum patrocinador encontrado.',
      activeOffers: 'Ofertas de patrocínio ativas',
      offersHint: 'Candidate-se a uma oferta pública. O patrocinador revisará sua candidatura antes da publicação.',
      proposal: 'Proposta',
      amount: 'Valor',
      minDur: 'Duração mínima: {n}s. Fale sobre o patrocinador por pelo menos 10s. Ele revisará suas estatísticas antes de validar.',
      willReview: 'O patrocinador revisará suas estatísticas antes de validar.',
      send: 'Enviar pedido',
      sending: 'Enviando…',
      applyOffer: 'Candidatar-se à oferta',
      applyTitle: 'Candidatura à oferta',
      talkAtLeast: 'Fale sobre o patrocinador por pelo menos {n}s.',
      byShort: 'Por',
      pickFirst: 'Escolha um patrocinador ou uma oferta',
      invalidAmount: 'Valor inválido',
      okSubmitted: 'Pedido enviado — aguardando validação',
      errRequest: 'Erro ao enviar'
    },
    mon: {
      title: 'Monetização',
      body: 'A partir de 5 000 seguidores, você pode anexar um patrocinador ao seu conteúdo e receber pagamentos diretos em mais de 15 moedas.',
      cta: 'Saiba mais',
      dismiss: 'fechar'
    }
  },
  de: {
    gim: {
      modalTitle: 'Gim Toli / Vocals',
      replyTo: 'Antwort an {name}',
      replyToDefault: 'ein Gim Toli',
      audio: 'Audio',
      tabRecord: 'Aufnehmen',
      tabUpload: 'Hochladen',
      pickAudioFile: 'Audiodatei wählen',
      audioLimits: 'Max 60 Sek • MP3, WAV, M4A usw.',
      recording: 'Aufnahme…',
      titlePlaceholder: 'Bsp: Meine Meinung zu diesem Film…',
      background: 'Hintergrundbild (optional)',
      backgroundPick: 'Bild wählen',
      effectsLabel: 'Audioeffekte (optional)',
      effectsApplying: 'Wird angewendet…',
      proVoices: 'Pro-Stimmen',
      rerecord: 'Neu aufnehmen',
      publish: 'Veröffentlichen',
      tapToRecord: 'Zum Aufnehmen die Taste drücken',
      micHint: '💡 Falls das Mikrofon nicht funktioniert, Zugriff in Browser-Einstellungen erlauben',
      errNoAudio: 'Kein Audio aufgenommen',
      errTooLong: 'Audio muss kürzer als {n} Sekunden sein',
      errNotAudio: 'Bitte Audiodatei wählen',
      errTooBig: 'Datei zu groß (max 10MB)',
      okLoaded: 'Audio erfolgreich geladen!',
      errUpload: 'Fehler beim Senden',
      errEffect: 'Effekt konnte nicht angewendet werden',
      okEffect: '{emoji} {label} angewendet',
      errMicDenied: 'Mikrofon-Zugriff verweigert. Klicke auf 🔒 in der Adressleiste.',
      errMicNotFound: 'Kein Mikrofon erkannt.',
      errMicBusy: 'Mikrofon wird bereits verwendet.',
      errMicGeneric: 'Kein Zugriff aufs Mikrofon. Stelle HTTPS und Berechtigungen sicher.'
    },
    pod: {
      modalTitle: 'Podcast erstellen',
      cover: 'Coverbild',
      add: 'Hinzufügen',
      title: 'Titel *',
      titlePlaceholder: 'Titel deines Podcasts',
      category: 'Kategorie',
      selectCategory: 'Kategorie wählen',
      guests: 'Gäste (durch Komma getrennt)',
      guestsPlaceholder: 'Max Mustermann, Anna Schmidt',
      audioFile: 'Audiodatei *',
      pickFile: 'Klicke, um eine Datei zu wählen',
      audioFormats: 'MP3, WAV, AAC (max 500 MB)',
      publish: 'Veröffentlichen',
      errNoAudio: 'Bitte Audiodatei wählen',
      errNoTitle: 'Titel erforderlich',
      errUpload: 'Fehler beim Senden',
      catCinema: 'Kino',
      catSeries: 'Serien',
      catMaking: 'Making-of',
      catTech: 'Tech',
      catMusic: 'Filmmusik',
      catInterviews: 'Interviews',
      catReviews: 'Kritiken'
    },
    story: {
      modalTitle: 'Neue Story',
      subtitle: 'Teile ein Bild oder Video unter 60 Sekunden',
      openCamera: 'Mit Kamera aufnehmen',
      tapToPick: 'Klicke zum Auswählen',
      hint: 'Bild oder Video (<60s)',
      images: 'Bilder',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Videos',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Ändern',
      publishing: 'Wird veröffentlicht…',
      publishRaw: 'So veröffentlichen',
      addText: 'Text hinzufügen',
      published: 'Story veröffentlicht!',
      errTooBig: 'Datei zu groß (max 50MB)',
      errFormat: 'Format nicht unterstützt',
      errTooLong: 'Video muss unter 60 Sekunden sein',
      errPick: 'Bitte Datei wählen',
      errPublish: 'Fehler beim Veröffentlichen'
    },
    spo: {
      title: 'Sponsor wählen',
      close: 'Schließen',
      lockedBanner: 'Monetarisierung ab 5 000 Follower. Du hast {n}. Du kannst ohne Sponsor teilen.',
      shareWithout: 'Ohne Sponsor teilen',
      searchPlaceholder: 'Sponsor suchen (verifizierter PRO-Account)…',
      onlyProNote: 'Nur verifizierte PRO-Accounts ✓ sind zulässig.',
      searching: 'Suche…',
      noResults: 'Kein Sponsor gefunden.',
      activeOffers: 'Aktive Sponsoring-Angebote',
      offersHint: 'Bewirb dich direkt auf ein öffentliches Angebot. Der Sponsor prüft vor der Veröffentlichung.',
      proposal: 'Vorschlag',
      amount: 'Betrag',
      minDur: 'Mindestdauer: {n}s. Mindestens 10s über den Sponsor sprechen. Er prüft deine Statistiken.',
      willReview: 'Der Sponsor prüft deine Statistiken vor der Freigabe.',
      send: 'Anfrage senden',
      sending: 'Sende…',
      applyOffer: 'Auf Angebot bewerben',
      applyTitle: 'Bewerbung auf Angebot',
      talkAtLeast: 'Mindestens {n}s über den Sponsor sprechen.',
      byShort: 'Von',
      pickFirst: 'Wähle einen Sponsor oder ein Angebot',
      invalidAmount: 'Ungültiger Betrag',
      okSubmitted: 'Anfrage gesendet — Bestätigung ausstehend',
      errRequest: 'Fehler beim Senden'
    },
    mon: {
      title: 'Monetarisierung',
      body: 'Ab 5 000 Followern kannst du einen Sponsor an deinen Content binden und Direktzahlungen in 15+ Währungen erhalten.',
      cta: 'Mehr erfahren',
      dismiss: 'schließen'
    }
  },
  ru: {
    gim: {
      modalTitle: 'Gim Toli / Голосовые',
      replyTo: 'В ответ на {name}',
      replyToDefault: 'Gim Toli',
      audio: 'Аудио',
      tabRecord: 'Записать',
      tabUpload: 'Загрузить',
      pickAudioFile: 'Выберите аудиофайл',
      audioLimits: 'Макс 60 сек • MP3, WAV, M4A и т. д.',
      recording: 'Идёт запись…',
      titlePlaceholder: 'Напр.: Моё мнение об этом фильме…',
      background: 'Фоновое изображение (необязательно)',
      backgroundPick: 'Выбрать изображение',
      effectsLabel: 'Аудиоэффекты (необязательно)',
      effectsApplying: 'Применяется…',
      proVoices: 'Профи-голоса',
      rerecord: 'Перезаписать',
      publish: 'Опубликовать',
      tapToRecord: 'Нажмите кнопку, чтобы начать запись',
      micHint: '💡 Если микрофон не работает, разрешите доступ в настройках браузера',
      errNoAudio: 'Аудио не записано',
      errTooLong: 'Аудио должно быть короче {n} секунд',
      errNotAudio: 'Выберите аудиофайл',
      errTooBig: 'Файл слишком большой (макс 10МБ)',
      okLoaded: 'Аудио успешно загружено!',
      errUpload: 'Ошибка отправки',
      errEffect: 'Не удалось применить эффект',
      okEffect: '{emoji} {label} применён',
      errMicDenied: 'Доступ к микрофону запрещён. Нажмите 🔒 в адресной строке.',
      errMicNotFound: 'Микрофон не обнаружен.',
      errMicBusy: 'Микрофон уже используется другим приложением.',
      errMicGeneric: 'Нет доступа к микрофону. Убедитесь в HTTPS и разрешениях.'
    },
    pod: {
      modalTitle: 'Создать подкаст',
      cover: 'Обложка',
      add: 'Добавить',
      title: 'Название *',
      titlePlaceholder: 'Название вашего подкаста',
      category: 'Категория',
      selectCategory: 'Выберите категорию',
      guests: 'Гости (через запятую)',
      guestsPlaceholder: 'Иван Петров, Анна Сидорова',
      audioFile: 'Аудиофайл *',
      pickFile: 'Нажмите, чтобы выбрать файл',
      audioFormats: 'MP3, WAV, AAC (макс 500 МБ)',
      publish: 'Опубликовать',
      errNoAudio: 'Выберите аудиофайл',
      errNoTitle: 'Требуется название',
      errUpload: 'Ошибка отправки',
      catCinema: 'Кино',
      catSeries: 'Сериалы',
      catMaking: 'Как это снималось',
      catTech: 'Технологии',
      catMusic: 'Музыка из фильмов',
      catInterviews: 'Интервью',
      catReviews: 'Обзоры'
    },
    story: {
      modalTitle: 'Новая история',
      subtitle: 'Поделитесь изображением или видео короче 60 секунд',
      openCamera: 'Снять камерой',
      tapToPick: 'Нажмите, чтобы выбрать',
      hint: 'Изображение или видео (<60с)',
      images: 'Изображения',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Видео',
      vidFormats: 'MP4, WebM (<60с)',
      change: 'Изменить',
      publishing: 'Публикация…',
      publishRaw: 'Опубликовать как есть',
      addText: 'Добавить текст',
      published: 'История опубликована!',
      errTooBig: 'Файл слишком большой (макс 50МБ)',
      errFormat: 'Неподдерживаемый формат',
      errTooLong: 'Видео должно быть короче 60 секунд',
      errPick: 'Выберите файл',
      errPublish: 'Ошибка публикации'
    },
    spo: {
      title: 'Выбрать спонсора',
      close: 'Закрыть',
      lockedBanner: 'Монетизация открывается с 5 000 подписчиков. У вас {n}. Можно поделиться и без спонсора.',
      shareWithout: 'Поделиться без спонсора',
      searchPlaceholder: 'Найти спонсора (верифиц. PRO-аккаунт)…',
      onlyProNote: 'Только верифицированные PRO-аккаунты ✓ доступны.',
      searching: 'Поиск…',
      noResults: 'Спонсоров не найдено.',
      activeOffers: 'Активные предложения спонсорства',
      offersHint: 'Откликнитесь на публичное предложение. Спонсор просмотрит заявку перед публикацией.',
      proposal: 'Предложение',
      amount: 'Сумма',
      minDur: 'Мин. длительность: {n}с. Говорите о спонсоре не менее 10с. Он проверит статистику.',
      willReview: 'Спонсор проверит статистику перед подтверждением.',
      send: 'Отправить заявку',
      sending: 'Отправка…',
      applyOffer: 'Откликнуться',
      applyTitle: 'Отклик на предложение',
      talkAtLeast: 'Говорите о спонсоре не менее {n}с.',
      byShort: 'От',
      pickFirst: 'Выберите спонсора или предложение',
      invalidAmount: 'Неверная сумма',
      okSubmitted: 'Заявка отправлена — ожидание проверки',
      errRequest: 'Ошибка отправки'
    },
    mon: {
      title: 'Монетизация',
      body: 'С 5 000 подписчиков можно прикрепить спонсора к контенту и получать прямые платежи в 15+ валютах.',
      cta: 'Подробнее',
      dismiss: 'закрыть'
    }
  },
  zh: {
    gim: {
      modalTitle: 'Gim Toli / 语音',
      replyTo: '回复 {name}',
      replyToDefault: '一条 Gim Toli',
      audio: '音频',
      tabRecord: '录制',
      tabUpload: '上传',
      pickAudioFile: '选择音频文件',
      audioLimits: '最长 60 秒 • MP3、WAV、M4A 等',
      recording: '正在录制…',
      titlePlaceholder: '例如：我对这部电影的看法…',
      background: '背景图片（可选）',
      backgroundPick: '选择图片',
      effectsLabel: '音频效果（可选）',
      effectsApplying: '应用中…',
      proVoices: '专业声音',
      rerecord: '重新录制',
      publish: '发布',
      tapToRecord: '按下按钮开始录制',
      micHint: '💡 如果麦克风不工作，请在浏览器设置中允许访问',
      errNoAudio: '未录制音频',
      errTooLong: '音频必须短于 {n} 秒',
      errNotAudio: '请选择音频文件',
      errTooBig: '文件过大（最大 10MB）',
      okLoaded: '音频加载成功！',
      errUpload: '上传出错',
      errEffect: '无法应用效果',
      okEffect: '{emoji} 已应用 {label}',
      errMicDenied: '麦克风权限被拒绝。点击地址栏中的 🔒 图标以允许。',
      errMicNotFound: '未检测到麦克风。',
      errMicBusy: '麦克风已被其他应用占用。',
      errMicGeneric: '无法访问麦克风。请确保使用 HTTPS 并授予权限。'
    },
    pod: {
      modalTitle: '创建播客',
      cover: '封面图片',
      add: '添加',
      title: '标题 *',
      titlePlaceholder: '你的播客标题',
      category: '分类',
      selectCategory: '选择分类',
      guests: '嘉宾（用逗号分隔）',
      guestsPlaceholder: '张三、李四',
      audioFile: '音频文件 *',
      pickFile: '点击选择文件',
      audioFormats: 'MP3、WAV、AAC（最大 500 MB）',
      publish: '发布',
      errNoAudio: '请选择音频文件',
      errNoTitle: '标题必填',
      errUpload: '上传出错',
      catCinema: '电影',
      catSeries: '剧集',
      catMaking: '幕后花絮',
      catTech: '科技',
      catMusic: '电影音乐',
      catInterviews: '访谈',
      catReviews: '评论'
    },
    story: {
      modalTitle: '新故事',
      subtitle: '分享一张图片或不超过 60 秒的视频',
      openCamera: '用相机拍摄',
      tapToPick: '点击选择',
      hint: '图片或视频（<60秒）',
      images: '图片',
      imgFormats: 'JPG、PNG、WebP',
      videos: '视频',
      vidFormats: 'MP4、WebM（<60秒）',
      change: '更换',
      publishing: '发布中…',
      publishRaw: '直接发布',
      addText: '添加文字',
      published: '故事已发布！',
      errTooBig: '文件过大（最大 50MB）',
      errFormat: '不支持的格式',
      errTooLong: '视频必须短于 60 秒',
      errPick: '请选择文件',
      errPublish: '发布出错'
    },
    spo: {
      title: '选择赞助商',
      close: '关闭',
      lockedBanner: '5,000 位关注者后解锁收益。当前 {n} 位。你仍可无赞助商分享。',
      shareWithout: '不带赞助商分享',
      searchPlaceholder: '搜索赞助商（认证 PRO 账号）…',
      onlyProNote: '仅认证 PRO 账号 ✓ 可参与。',
      searching: '搜索中…',
      noResults: '未找到赞助商。',
      activeOffers: '进行中的赞助报价',
      offersHint: '直接申请公开报价。赞助商将在发布前审核你的申请。',
      proposal: '提案',
      amount: '金额',
      minDur: '最低时长：{n}秒。你必须谈论赞助商至少 10 秒。赞助商将审核你的数据。',
      willReview: '赞助商将在确认前审核你的数据。',
      send: '发送请求',
      sending: '发送中…',
      applyOffer: '申请此报价',
      applyTitle: '报价申请',
      talkAtLeast: '你必须谈论赞助商至少 {n} 秒。',
      byShort: '由',
      pickFirst: '选择一个赞助商或报价',
      invalidAmount: '金额无效',
      okSubmitted: '请求已发送 — 等待审核',
      errRequest: '发送时出错'
    },
    mon: {
      title: '内容变现',
      body: '达到 5,000 位关注者后，你可以为内容附加赞助商，并以 15+ 种货币接收直付款。',
      cta: '了解更多',
      dismiss: '关闭'
    }
  },
  ja: {
    gim: {
      modalTitle: 'Gim Toli / ボイス',
      replyTo: '{name} への返信',
      replyToDefault: 'Gim Toli',
      audio: 'オーディオ',
      tabRecord: '録音',
      tabUpload: 'アップロード',
      pickAudioFile: '音声ファイルを選択',
      audioLimits: '最大60秒 • MP3、WAV、M4A など',
      recording: '録音中…',
      titlePlaceholder: '例：この映画についての私の感想…',
      background: '背景画像（任意）',
      backgroundPick: '画像を選択',
      effectsLabel: 'オーディオエフェクト（任意）',
      effectsApplying: '適用中…',
      proVoices: 'プロの声',
      rerecord: '録音し直す',
      publish: '公開',
      tapToRecord: 'ボタンを押して録音を開始',
      micHint: '💡 マイクが動作しない場合はブラウザ設定でアクセスを許可',
      errNoAudio: '録音がありません',
      errTooLong: 'オーディオは{n}秒未満にしてください',
      errNotAudio: '音声ファイルを選択してください',
      errTooBig: 'ファイルが大きすぎます（最大10MB）',
      okLoaded: 'オーディオを読み込みました！',
      errUpload: '送信エラー',
      errEffect: 'エフェクトを適用できません',
      okEffect: '{emoji} {label} を適用',
      errMicDenied: 'マイクの権限が拒否されました。アドレスバーの 🔒 をクリックしてください。',
      errMicNotFound: 'マイクが検出されません。',
      errMicBusy: 'マイクは他のアプリで使用中です。',
      errMicGeneric: 'マイクにアクセスできません。HTTPS と権限を確認してください。'
    },
    pod: {
      modalTitle: 'ポッドキャストを作成',
      cover: 'カバー画像',
      add: '追加',
      title: 'タイトル *',
      titlePlaceholder: 'ポッドキャストのタイトル',
      category: 'カテゴリー',
      selectCategory: 'カテゴリーを選択',
      guests: 'ゲスト（カンマ区切り）',
      guestsPlaceholder: '山田太郎、田中花子',
      audioFile: '音声ファイル *',
      pickFile: 'クリックしてファイルを選択',
      audioFormats: 'MP3、WAV、AAC（最大500 MB）',
      publish: '公開',
      errNoAudio: '音声ファイルを選択してください',
      errNoTitle: 'タイトルは必須です',
      errUpload: '送信エラー',
      catCinema: '映画',
      catSeries: 'シリーズ',
      catMaking: 'メイキング',
      catTech: 'テック',
      catMusic: '映画音楽',
      catInterviews: 'インタビュー',
      catReviews: 'レビュー'
    },
    story: {
      modalTitle: '新しいストーリー',
      subtitle: '60秒未満の画像または動画を共有',
      openCamera: 'カメラで撮影',
      tapToPick: 'クリックして選択',
      hint: '画像または動画（60秒未満）',
      images: '画像',
      imgFormats: 'JPG、PNG、WebP',
      videos: '動画',
      vidFormats: 'MP4、WebM（60秒未満）',
      change: '変更',
      publishing: '公開中…',
      publishRaw: 'そのまま公開',
      addText: 'テキストを追加',
      published: 'ストーリーを公開しました！',
      errTooBig: 'ファイルが大きすぎます（最大50MB）',
      errFormat: 'サポートされない形式',
      errTooLong: '動画は60秒未満にしてください',
      errPick: 'ファイルを選択してください',
      errPublish: '公開エラー'
    },
    spo: {
      title: 'スポンサーを選ぶ',
      close: '閉じる',
      lockedBanner: '5,000フォロワーで収益化が解放されます。現在 {n} フォロワー。スポンサーなしで共有もできます。',
      shareWithout: 'スポンサーなしで共有',
      searchPlaceholder: 'スポンサー検索（認証済みPROアカウント）…',
      onlyProNote: '認証済みPROアカウント ✓ のみ対象。',
      searching: '検索中…',
      noResults: 'スポンサーが見つかりません。',
      activeOffers: '有効なスポンサー案件',
      offersHint: '公開オファーに直接応募。スポンサーが公開前に確認します。',
      proposal: '提案',
      amount: '金額',
      minDur: '最低時間：{n}秒。スポンサーについて少なくとも10秒話す必要があります。統計を審査します。',
      willReview: 'スポンサーが承認前に統計を確認します。',
      send: 'リクエスト送信',
      sending: '送信中…',
      applyOffer: 'オファーに応募',
      applyTitle: 'オファー応募',
      talkAtLeast: 'スポンサーについて少なくとも {n} 秒話してください。',
      byShort: '提供元:',
      pickFirst: 'スポンサーまたはオファーを選択',
      invalidAmount: '金額が無効です',
      okSubmitted: 'リクエスト送信済み — 承認待ち',
      errRequest: '送信エラー'
    },
    mon: {
      title: '収益化',
      body: '5,000フォロワーからコンテンツにスポンサーを付けて、15以上の通貨で直接支払いを受けられます。',
      cta: '詳細を見る',
      dismiss: '閉じる'
    }
  },
  hi: {
    gim: {
      modalTitle: 'Gim Toli / वॉइस',
      replyTo: '{name} को उत्तर',
      replyToDefault: 'एक Gim Toli',
      audio: 'ऑडियो',
      tabRecord: 'रिकॉर्ड',
      tabUpload: 'अपलोड',
      pickAudioFile: 'ऑडियो फ़ाइल चुनें',
      audioLimits: 'अधिकतम 60 सेकंड • MP3, WAV, M4A आदि',
      recording: 'रिकॉर्ड हो रहा है…',
      titlePlaceholder: 'उदा.: इस फ़िल्म पर मेरी राय…',
      background: 'बैकग्राउंड इमेज (वैकल्पिक)',
      backgroundPick: 'इमेज चुनें',
      effectsLabel: 'ऑडियो इफेक्ट्स (वैकल्पिक)',
      effectsApplying: 'लागू हो रहा है…',
      proVoices: 'प्रो आवाज़ें',
      rerecord: 'फिर से रिकॉर्ड करें',
      publish: 'प्रकाशित करें',
      tapToRecord: 'रिकॉर्डिंग शुरू करने के लिए बटन दबाएँ',
      micHint: '💡 माइक काम नहीं कर रहा तो ब्राउज़र सेटिंग्स में अनुमति दें',
      errNoAudio: 'कोई ऑडियो रिकॉर्ड नहीं हुआ',
      errTooLong: 'ऑडियो {n} सेकंड से कम होना चाहिए',
      errNotAudio: 'कृपया ऑडियो फ़ाइल चुनें',
      errTooBig: 'फ़ाइल बहुत बड़ी है (अधिकतम 10MB)',
      okLoaded: 'ऑडियो सफलतापूर्वक लोड हुआ!',
      errUpload: 'अपलोड में त्रुटि',
      errEffect: 'इफेक्ट लागू नहीं हो सका',
      okEffect: '{emoji} {label} लागू',
      errMicDenied: 'माइक्रोफ़ोन अनुमति अस्वीकृत। एड्रेस बार में 🔒 पर क्लिक करें।',
      errMicNotFound: 'कोई माइक्रोफ़ोन नहीं मिला।',
      errMicBusy: 'माइक्रोफ़ोन पहले से किसी अन्य ऐप में उपयोग में है।',
      errMicGeneric: 'माइक्रोफ़ोन तक पहुँच नहीं। HTTPS और अनुमतियाँ सुनिश्चित करें।'
    },
    pod: {
      modalTitle: 'पॉडकास्ट बनाएँ',
      cover: 'कवर इमेज',
      add: 'जोड़ें',
      title: 'शीर्षक *',
      titlePlaceholder: 'आपके पॉडकास्ट का शीर्षक',
      category: 'श्रेणी',
      selectCategory: 'श्रेणी चुनें',
      guests: 'अतिथि (कॉमा से अलग)',
      guestsPlaceholder: 'राहुल शर्मा, प्रिया पटेल',
      audioFile: 'ऑडियो फ़ाइल *',
      pickFile: 'फ़ाइल चुनने के लिए क्लिक करें',
      audioFormats: 'MP3, WAV, AAC (अधिकतम 500 MB)',
      publish: 'प्रकाशित करें',
      errNoAudio: 'कृपया ऑडियो फ़ाइल चुनें',
      errNoTitle: 'शीर्षक आवश्यक है',
      errUpload: 'अपलोड में त्रुटि',
      catCinema: 'सिनेमा',
      catSeries: 'सीरीज़',
      catMaking: 'मेकिंग-ऑफ',
      catTech: 'टेक',
      catMusic: 'फिल्म संगीत',
      catInterviews: 'इंटरव्यू',
      catReviews: 'समीक्षा'
    },
    story: {
      modalTitle: 'नई स्टोरी',
      subtitle: '60 सेकंड से कम की छवि या वीडियो साझा करें',
      openCamera: 'कैमरे से लें',
      tapToPick: 'चुनने के लिए क्लिक करें',
      hint: 'छवि या वीडियो (<60s)',
      images: 'छवियाँ',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'वीडियो',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'बदलें',
      publishing: 'प्रकाशित हो रहा…',
      publishRaw: 'ऐसे ही प्रकाशित',
      addText: 'टेक्स्ट जोड़ें',
      published: 'स्टोरी प्रकाशित!',
      errTooBig: 'फ़ाइल बहुत बड़ी है (अधिकतम 50MB)',
      errFormat: 'असमर्थित फ़ॉर्मैट',
      errTooLong: 'वीडियो 60 सेकंड से कम होना चाहिए',
      errPick: 'कृपया फ़ाइल चुनें',
      errPublish: 'प्रकाशन में त्रुटि'
    },
    spo: {
      title: 'प्रायोजक चुनें',
      close: 'बंद करें',
      lockedBanner: 'मुद्रीकरण 5,000 फॉलोअर्स पर अनलॉक होता है। आपके {n} हैं। बिना प्रायोजक भी साझा कर सकते हैं।',
      shareWithout: 'बिना प्रायोजक साझा करें',
      searchPlaceholder: 'प्रायोजक खोजें (सत्यापित PRO)…',
      onlyProNote: 'केवल सत्यापित PRO ✓ खाते पात्र हैं।',
      searching: 'खोज रहा है…',
      noResults: 'कोई प्रायोजक नहीं मिला।',
      activeOffers: 'सक्रिय प्रायोजन ऑफ़र',
      offersHint: 'सार्वजनिक ऑफ़र पर सीधे आवेदन करें। प्रायोजक प्रकाशन से पहले समीक्षा करेगा।',
      proposal: 'प्रस्ताव',
      amount: 'राशि',
      minDur: 'न्यूनतम अवधि: {n}s। प्रायोजक के बारे में कम से कम 10s बोलें। वे आपके आँकड़ों की समीक्षा करेंगे।',
      willReview: 'प्रायोजक पुष्टि से पहले आँकड़ों की समीक्षा करेगा।',
      send: 'अनुरोध भेजें',
      sending: 'भेजा जा रहा…',
      applyOffer: 'ऑफ़र पर आवेदन',
      applyTitle: 'ऑफ़र आवेदन',
      talkAtLeast: 'प्रायोजक के बारे में कम से कम {n}s बोलें।',
      byShort: 'द्वारा',
      pickFirst: 'एक प्रायोजक या ऑफ़र चुनें',
      invalidAmount: 'अमान्य राशि',
      okSubmitted: 'अनुरोध भेजा गया — पुष्टि की प्रतीक्षा',
      errRequest: 'भेजने में त्रुटि'
    },
    mon: {
      title: 'मुद्रीकरण',
      body: '5,000 फॉलोअर्स से आप अपने कंटेंट में प्रायोजक जोड़ सकते हैं और 15+ मुद्राओं में सीधे भुगतान पा सकते हैं।',
      cta: 'और जानें',
      dismiss: 'बंद करें'
    }
  },
  nl: {
    gim: {
      modalTitle: 'Gim Toli / Vocaal',
      replyTo: 'Antwoord aan {name}',
      replyToDefault: 'een Gim Toli',
      audio: 'Audio',
      tabRecord: 'Opnemen',
      tabUpload: 'Uploaden',
      pickAudioFile: 'Kies een audiobestand',
      audioLimits: 'Max 60 sec • MP3, WAV, M4A, enz.',
      recording: 'Opnemen…',
      titlePlaceholder: 'Bijv.: Mijn mening over deze film…',
      background: 'Achtergrondafbeelding (optioneel)',
      backgroundPick: 'Kies een afbeelding',
      effectsLabel: 'Audio-effecten (optioneel)',
      effectsApplying: 'Toepassen…',
      proVoices: 'Pro-stemmen',
      rerecord: 'Opnieuw opnemen',
      publish: 'Publiceren',
      tapToRecord: 'Druk op de knop om op te nemen',
      micHint: '💡 Werkt de microfoon niet, sta toegang toe in de browserinstellingen',
      errNoAudio: 'Geen audio opgenomen',
      errTooLong: 'Audio moet korter zijn dan {n} seconden',
      errNotAudio: 'Selecteer een audiobestand',
      errTooBig: 'Bestand te groot (max 10MB)',
      okLoaded: 'Audio geladen!',
      errUpload: 'Fout bij verzenden',
      errEffect: 'Kon effect niet toepassen',
      okEffect: '{emoji} {label} toegepast',
      errMicDenied: 'Microfoontoegang geweigerd. Klik op 🔒 in de adresbalk.',
      errMicNotFound: 'Geen microfoon gedetecteerd.',
      errMicBusy: 'Microfoon wordt al gebruikt.',
      errMicGeneric: 'Geen toegang tot microfoon. Zorg voor HTTPS en machtigingen.'
    },
    pod: {
      modalTitle: 'Podcast maken',
      cover: 'Omslagafbeelding',
      add: 'Toevoegen',
      title: 'Titel *',
      titlePlaceholder: 'De titel van je podcast',
      category: 'Categorie',
      selectCategory: 'Kies een categorie',
      guests: 'Gasten (komma’s scheiden)',
      guestsPlaceholder: 'Jan de Vries, Anna Jansen',
      audioFile: 'Audiobestand *',
      pickFile: 'Klik om een bestand te kiezen',
      audioFormats: 'MP3, WAV, AAC (max 500 MB)',
      publish: 'Publiceren',
      errNoAudio: 'Selecteer een audiobestand',
      errNoTitle: 'Titel vereist',
      errUpload: 'Fout bij verzenden',
      catCinema: 'Cinema',
      catSeries: 'Series',
      catMaking: 'Making-of',
      catTech: 'Tech',
      catMusic: 'Filmmuziek',
      catInterviews: 'Interviews',
      catReviews: 'Recensies'
    },
    story: {
      modalTitle: 'Nieuwe Story',
      subtitle: 'Deel een afbeelding of video onder 60 seconden',
      openCamera: 'Foto met camera',
      tapToPick: 'Klik om te kiezen',
      hint: 'Afbeelding of video (<60s)',
      images: 'Afbeeldingen',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Video’s',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Wijzigen',
      publishing: 'Publiceren…',
      publishRaw: 'Zo publiceren',
      addText: 'Tekst toevoegen',
      published: 'Story gepubliceerd!',
      errTooBig: 'Bestand te groot (max 50MB)',
      errFormat: 'Formaat niet ondersteund',
      errTooLong: 'Video moet korter zijn dan 60 seconden',
      errPick: 'Selecteer een bestand',
      errPublish: 'Publicatiefout'
    },
    spo: {
      title: 'Kies een sponsor',
      close: 'Sluiten',
      lockedBanner: 'Monetisering vanaf 5.000 volgers. Je hebt er {n}. Delen zonder sponsor kan.',
      shareWithout: 'Delen zonder sponsor',
      searchPlaceholder: 'Zoek een sponsor (geverifieerde PRO)…',
      onlyProNote: 'Alleen geverifieerde PRO-accounts ✓ zijn geschikt.',
      searching: 'Zoeken…',
      noResults: 'Geen sponsor gevonden.',
      activeOffers: 'Actieve sponsoraanbiedingen',
      offersHint: 'Reageer direct op een openbaar aanbod. De sponsor beoordeelt vóór publicatie.',
      proposal: 'Voorstel',
      amount: 'Bedrag',
      minDur: 'Min. duur: {n}s. Spreek min. 10s over de sponsor. Statistieken worden vooraf gecontroleerd.',
      willReview: 'De sponsor beoordeelt je statistieken vóór goedkeuring.',
      send: 'Verzoek versturen',
      sending: 'Verzenden…',
      applyOffer: 'Solliciteer op aanbod',
      applyTitle: 'Sollicitatie op aanbod',
      talkAtLeast: 'Spreek min. {n}s over de sponsor.',
      byShort: 'Door',
      pickFirst: 'Kies een sponsor of aanbod',
      invalidAmount: 'Ongeldig bedrag',
      okSubmitted: 'Verzoek verstuurd — in afwachting',
      errRequest: 'Fout bij verzenden'
    },
    mon: {
      title: 'Monetisering',
      body: 'Vanaf 5.000 volgers kun je een sponsor koppelen aan je content en directe betalingen in 15+ valuta ontvangen.',
      cta: 'Meer info',
      dismiss: 'sluiten'
    }
  },
  sw: {
    gim: {
      modalTitle: 'Gim Toli / Sauti',
      replyTo: 'Jibu kwa {name}',
      replyToDefault: 'Gim Toli',
      audio: 'Sauti',
      tabRecord: 'Rekodi',
      tabUpload: 'Pakia',
      pickAudioFile: 'Chagua faili la sauti',
      audioLimits: 'Sekunde 60 juu • MP3, WAV, M4A, n.k.',
      recording: 'Inarekodi…',
      titlePlaceholder: 'Mfano: Maoni yangu kuhusu filamu hii…',
      background: 'Picha ya mandharinyuma (hiari)',
      backgroundPick: 'Chagua picha',
      effectsLabel: 'Athari za sauti (hiari)',
      effectsApplying: 'Inatekeleza…',
      proVoices: 'Sauti za kitaalamu',
      rerecord: 'Rekodi upya',
      publish: 'Chapisha',
      tapToRecord: 'Bofya kitufe kuanza kurekodi',
      micHint: '💡 Kama maikrofoni haifanyi kazi, ruhusu ufikiaji katika mipangilio ya kivinjari',
      errNoAudio: 'Hakuna sauti iliyorekodiwa',
      errTooLong: 'Sauti lazima iwe chini ya sekunde {n}',
      errNotAudio: 'Tafadhali chagua faili la sauti',
      errTooBig: 'Faili kubwa mno (upeo 10MB)',
      okLoaded: 'Sauti imepakiwa!',
      errUpload: 'Hitilafu ya kupakia',
      errEffect: 'Athari haikutumika',
      okEffect: '{emoji} {label} imetumika',
      errMicDenied: 'Ruhusa ya maikrofoni imekataliwa. Bofya 🔒 kwenye upau wa anwani.',
      errMicNotFound: 'Hakuna maikrofoni iliyogunduliwa.',
      errMicBusy: 'Maikrofoni inatumika na programu nyingine.',
      errMicGeneric: 'Haiwezi kufikia maikrofoni. Hakikisha HTTPS na ruhusa.'
    },
    pod: {
      modalTitle: 'Unda Podcast',
      cover: 'Picha ya jalada',
      add: 'Ongeza',
      title: 'Kichwa *',
      titlePlaceholder: 'Kichwa cha podcast yako',
      category: 'Kikundi',
      selectCategory: 'Chagua kikundi',
      guests: 'Wageni (tenganisha kwa koma)',
      guestsPlaceholder: 'Juma Mwangi, Amina Ali',
      audioFile: 'Faili la sauti *',
      pickFile: 'Bofya kuchagua faili',
      audioFormats: 'MP3, WAV, AAC (upeo 500 MB)',
      publish: 'Chapisha',
      errNoAudio: 'Tafadhali chagua faili la sauti',
      errNoTitle: 'Kichwa kinahitajika',
      errUpload: 'Hitilafu ya kupakia',
      catCinema: 'Sinema',
      catSeries: 'Mfululizo',
      catMaking: 'Nyuma ya pazia',
      catTech: 'Teknolojia',
      catMusic: 'Muziki wa filamu',
      catInterviews: 'Mahojiano',
      catReviews: 'Uhakiki'
    },
    story: {
      modalTitle: 'Story mpya',
      subtitle: 'Shiriki picha au video chini ya sekunde 60',
      openCamera: 'Piga na kamera',
      tapToPick: 'Bofya kuchagua',
      hint: 'Picha au video (<60s)',
      images: 'Picha',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Video',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Badilisha',
      publishing: 'Inachapisha…',
      publishRaw: 'Chapisha kama ilivyo',
      addText: 'Ongeza maandishi',
      published: 'Story imechapishwa!',
      errTooBig: 'Faili kubwa mno (upeo 50MB)',
      errFormat: 'Muundo hauendani',
      errTooLong: 'Video lazima iwe chini ya sekunde 60',
      errPick: 'Tafadhali chagua faili',
      errPublish: 'Hitilafu ya kuchapisha'
    },
    spo: {
      title: 'Chagua mfadhili',
      close: 'Funga',
      lockedBanner: 'Uchumaji hufunguliwa kwa wafuasi 5,000. Una {n}. Bado unaweza kushiriki bila mfadhili.',
      shareWithout: 'Shiriki bila mfadhili',
      searchPlaceholder: 'Tafuta mfadhili (akaunti PRO iliyothibitishwa)…',
      onlyProNote: 'Ni akaunti PRO ✓ pekee zinazokubalika.',
      searching: 'Inatafuta…',
      noResults: 'Hakuna mfadhili aliyepatikana.',
      activeOffers: 'Ofa hai za ufadhili',
      offersHint: 'Omba moja kwa moja kwenye ofa ya umma. Mfadhili atakagua kabla ya kuchapisha.',
      proposal: 'Pendekezo',
      amount: 'Kiasi',
      minDur: 'Muda wa chini: {n}s. Zungumza kuhusu mfadhili angalau sekunde 10. Atakagua takwimu zako.',
      willReview: 'Mfadhili atakagua takwimu zako kabla ya uthibitisho.',
      send: 'Tuma ombi',
      sending: 'Inatuma…',
      applyOffer: 'Omba ofa',
      applyTitle: 'Ombi la ofa',
      talkAtLeast: 'Zungumza kuhusu mfadhili angalau {n}s.',
      byShort: 'Na',
      pickFirst: 'Chagua mfadhili au ofa',
      invalidAmount: 'Kiasi si sahihi',
      okSubmitted: 'Ombi limetumwa — kusubiri uthibitisho',
      errRequest: 'Hitilafu ya kutuma'
    },
    mon: {
      title: 'Uchumaji',
      body: 'Kuanzia wafuasi 5,000 unaweza kuunganisha mfadhili kwenye maudhui yako na kupokea malipo ya moja kwa moja katika sarafu 15+.',
      cta: 'Jifunze zaidi',
      dismiss: 'funga'
    }
  },
  ht: {
    gim: {
      modalTitle: 'Gim Toli / Vokal',
      replyTo: 'Repons pou {name}',
      replyToDefault: 'yon Gim Toli',
      audio: 'Odyo',
      tabRecord: 'Anrejistre',
      tabUpload: 'Telechaje',
      pickAudioFile: 'Chwazi yon fichye odyo',
      audioLimits: 'Maks 60 segond • MP3, WAV, M4A, elatriye',
      recording: 'K ap anrejistre…',
      titlePlaceholder: 'Egz: Sa m panse sou fim sa a…',
      background: 'Imaj fon (opsyonèl)',
      backgroundPick: 'Chwazi yon imaj',
      effectsLabel: 'Efè odyo (opsyonèl)',
      effectsApplying: 'Aplike…',
      proVoices: 'Vwa pro',
      rerecord: 'Refè anrejistreman',
      publish: 'Pibliye',
      tapToRecord: 'Peze bouton an pou kòmanse anrejistre',
      micHint: '💡 Si mikro pa mache, otorize aksè nan paramèt navigatè a',
      errNoAudio: 'Pa gen odyo anrejistre',
      errTooLong: 'Odyo dwe pi kout pase {n} segond',
      errNotAudio: 'Silvouplè chwazi yon fichye odyo',
      errTooBig: 'Fichye a twò gwo (maks 10MB)',
      okLoaded: 'Odyo chaje avèk siksè!',
      errUpload: 'Erè pandan voye',
      errEffect: 'Efè pa aplike',
      okEffect: '{emoji} {label} aplike',
      errMicDenied: 'Pèmisyon mikro refize. Klike sou 🔒 nan ba adrès la.',
      errMicNotFound: 'Pa gen mikro detekte.',
      errMicBusy: 'Mikro deja itilize pa yon lòt app.',
      errMicGeneric: 'Pa ka jwenn mikro. Asire ke HTTPS ak pèmisyon aktif.'
    },
    pod: {
      modalTitle: 'Kreye yon Podcast',
      cover: 'Imaj kouvèti',
      add: 'Ajoute',
      title: 'Tit *',
      titlePlaceholder: 'Tit podcast ou',
      category: 'Kategori',
      selectCategory: 'Chwazi yon kategori',
      guests: 'Envite (separe pa vigil)',
      guestsPlaceholder: 'Jak Pierre, Mari Jean',
      audioFile: 'Fichye odyo *',
      pickFile: 'Klike pou chwazi fichye',
      audioFormats: 'MP3, WAV, AAC (maks 500 MB)',
      publish: 'Pibliye',
      errNoAudio: 'Silvouplè chwazi yon fichye odyo',
      errNoTitle: 'Tit obligatwa',
      errUpload: 'Erè pandan voye',
      catCinema: 'Sinema',
      catSeries: 'Seri',
      catMaking: 'Making-of',
      catTech: 'Teknoloji',
      catMusic: 'Mizik fim',
      catInterviews: 'Entèvyou',
      catReviews: 'Kritik'
    },
    story: {
      modalTitle: 'Nouvo Story',
      subtitle: 'Pataje yon imaj oswa yon videyo mwens ke 60 segond',
      openCamera: 'Pran ak kamera',
      tapToPick: 'Klike pou chwazi',
      hint: 'Imaj oswa videyo (<60s)',
      images: 'Imaj',
      imgFormats: 'JPG, PNG, WebP',
      videos: 'Videyo',
      vidFormats: 'MP4, WebM (<60s)',
      change: 'Chanje',
      publishing: 'Ap pibliye…',
      publishRaw: 'Pibliye jan li ye',
      addText: 'Ajoute tèks',
      published: 'Story pibliye!',
      errTooBig: 'Fichye twò gwo (maks 50MB)',
      errFormat: 'Fòma pa sipòte',
      errTooLong: 'Videyo dwe pi kout pase 60 segond',
      errPick: 'Silvouplè chwazi yon fichye',
      errPublish: 'Erè pibliyasyon'
    },
    spo: {
      title: 'Chwazi yon esponsò',
      close: 'Fèmen',
      lockedBanner: 'Monetizasyon debloke a 5 000 abonnè. Ou gen {n}. Ou toujou ka pataje san esponsò.',
      shareWithout: 'Pataje san esponsò',
      searchPlaceholder: 'Chèche yon esponsò (kont PRO verifye)…',
      onlyProNote: 'Sèl kont PRO ✓ verifye yo elijib.',
      searching: 'Chèche…',
      noResults: 'Pa jwenn okenn esponsò.',
      activeOffers: 'Òf esponsò aktif',
      offersHint: 'Aplike dirèkteman sou yon òf piblik. Esponsò a ap egzamine avan piblikasyon.',
      proposal: 'Pwopozisyon',
      amount: 'Montan',
      minDur: 'Dire minimòm: {n}s. Ou dwe pale sou esponsò omwen 10s. L ap tcheke estatistik ou avan validasyon.',
      willReview: 'Esponsò a ap tcheke estatistik ou avan validasyon.',
      send: 'Voye demann',
      sending: 'Voye…',
      applyOffer: "Aplike sou òf la",
      applyTitle: "Aplikasyon pou òf la",
      talkAtLeast: 'Pale sou esponsò omwen {n}s.',
      byShort: 'Pa',
      pickFirst: 'Chwazi yon esponsò oswa yon òf',
      invalidAmount: 'Montan pa valid',
      okSubmitted: 'Demann voye — ap tann validasyon',
      errRequest: 'Erè pou voye'
    },
    mon: {
      title: 'Monetizasyon',
      body: 'Apati 5 000 abonnè, ou ka atache yon esponsò ak kontni ou epi resevwa peman dirèk nan plis pase 15 monnen.',
      cta: 'Aprann plis',
      dismiss: 'fèmen'
    }
  }
};

export const useCT = () => {
  const { language } = useLanguage();
  return (key, vars) => {
    const parts = key.split('.');
    let v = creationTranslations[language];
    for (const p of parts) v = v?.[p];
    if (v == null) {
      v = creationTranslations.en;
      for (const p of parts) v = v?.[p];
    }
    if (typeof v !== 'string') return key;
    if (vars) {
      return v.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
    }
    return v;
  };
};

export default creationTranslations;
